import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, UserCog, Users, Upload, BookOpen, Bell, Settings, GraduationCap, Award } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function AppShell({ children, section = "user" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuper = user?.role === "super_admin";

  const adminLinks = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: "Overview" },
    { to: "/admin/pending", icon: Bell, label: "Pending" },
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/bulk-upload", icon: Upload, label: "Bulk upload" },
    { to: "/admin/modules", icon: BookOpen, label: "Modules" },
    ...(isSuper ? [{ to: "/admin/settings", icon: Settings, label: "Settings" }] : []),
  ];

  const userLinks = [
    { to: "/dashboard", end: true, icon: LayoutDashboard, label: "My learning" },
    { to: "/certificates", icon: Award, label: "Certificates" },
    { to: "/profile", icon: UserCog, label: "Profile" },
  ];

  const links = section === "admin" ? adminLinks : userLinks;

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-white">
        <div className="h-16 flex items-center px-5 border-b border-border">
          <Link to={isAdmin ? "/admin" : "/dashboard"}><Logo /></Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 h-10 px-3 text-[13px] font-semibold transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-accent"
                }`
              }
            >
              <l.icon className="h-4 w-4" aria-hidden="true" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          {isAdmin && section === "user" && (
            <Link to="/admin" className="flex items-center gap-2 h-10 px-3 text-[13px] font-semibold text-primary hover:bg-accent border border-border mb-2">
              <GraduationCap className="h-4 w-4" /> Admin console
            </Link>
          )}
          {section === "admin" && (
            <Link to="/dashboard" className="flex items-center gap-2 h-10 px-3 text-[13px] font-semibold text-primary hover:bg-accent border border-border mb-2">
              <GraduationCap className="h-4 w-4" /> Learner view
            </Link>
          )}
          <div className="p-3 border border-border mb-2">
            <div className="overline">Signed in as</div>
            <div className="font-display font-bold text-[13px] mt-1 truncate">{user?.full_name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            <div className="mt-2 sharp-badge text-[10px]">{user?.role}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 h-10 px-3 text-[13px] font-semibold text-foreground hover:bg-accent border border-border"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden h-16 border-b border-border bg-white flex items-center justify-between px-5">
          <Link to={isAdmin ? "/admin" : "/dashboard"}><Logo /></Link>
          <button onClick={handleLogout} className="p-2 hover:bg-accent" aria-label="Sign out" data-testid="logout-button-mobile">
            <LogOut className="h-4 w-4" />
          </button>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
